import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

const SuccessfulCampaignCaseStudies = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });
  const [selectedCase, setSelectedCase] = useState(null);

  const caseStudies = [
    {
      id: 1,
      title: 'MP Assembly Elections 2023',
      subtitle: 'Madhya Pradesh State Campaign',
      category: 'state',
      duration: '6 months',
      description: 'Comprehensive campaign strategy covering 230 constituencies with targeted social media outreach and booth-level organization.',
      previousVote: 32,
      achievedVote: 48,
      margin: '+16% increase',
      gradient: 'from-blue-500 to-red-500',
      status: 'VICTORY',
      details: {
        overview: 'A comprehensive digital-first campaign strategy for Madhya Pradesh Assembly Elections 2023, covering 230 constituencies with targeted messaging and grassroots mobilization.',
        strategies: [
          'Constituency-wise voter analysis and micro-targeting',
          'Multi-language social media campaigns',
          'Booth-level volunteer training programs',
          'Real-time sentiment monitoring and response',
          'Traditional media integration with digital campaigns'
        ],
        results: [
          '48% vote share achieved (up from 32%)',
          '142 seats won out of 230 constituencies',
          '2.3M+ social media reach',
          '15,000+ trained volunteers',
          '89% booth coverage achieved'
        ],
        testimonial: {
          text: "Jet Digital Infotech's strategic approach and digital expertise were instrumental in our historic victory. Their data-driven methodology and ground-level execution made all the difference.",
          author: "Campaign Manager, MP Assembly Elections 2023"
        }
      }
    },
    {
      id: 2,
      title: 'Lok Sabha Campaign 2024',
      subtitle: 'Parliamentary Constituency',
      category: 'lok-sabha',
      duration: '8 months',
      description: 'Multi-constituency approach with coordinated messaging, influencer partnerships, and grassroots mobilization.',
      previousVote: 45,
      achievedVote: 62,
      margin: '1.2L votes',
      gradient: 'from-orange-500 to-purple-500',
      status: 'VICTORY',
      details: {
        overview: 'National-level parliamentary campaign with strategic coordination across multiple constituencies and comprehensive digital outreach.',
        strategies: [
          'Multi-constituency coordination strategy',
          'Influencer and celebrity endorsements',
          'Grassroots volunteer mobilization',
          'Data-driven voter targeting',
          'Cross-platform digital campaigns'
        ],
        results: [
          '62% vote share achieved (up from 45%)',
          'Victory margin of 1.2 lakh votes',
          '5.8M+ digital impressions',
          '25,000+ active volunteers',
          '95% polling booth coverage'
        ],
        testimonial: {
          text: "The strategic digital campaign and ground coordination delivered exceptional results. Their innovative approach to voter engagement was game-changing.",
          author: "Parliamentary Candidate, Lok Sabha 2024"
        }
      }
    }
  ];

  const openModal = (caseStudy) => {
    setSelectedCase(caseStudy);
  };

  const closeModal = () => {
    setSelectedCase(null);
  };

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        {/* Section Header */}
        <div className="text-center mb-16">
        <motion.h2
            ref={ref}
            className="display-text text-center text-gray-900 mb-6"
            initial={{ opacity: 0, y: 50 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.2, delay: 0.2 }}
          >
            Successful Campaign
            <span className="text-gradient-political"> Case Studies</span>
          </motion.h2>
        </div>

        {/* Case Studies Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {caseStudies.map((caseStudy) => (
            <div
              key={caseStudy.id}
              className="bg-white rounded-3xl shadow-xl overflow-hidden group cursor-pointer hover:shadow-2xl transition-all duration-200"
              onClick={() => openModal(caseStudy)}
            >
              {/* Header with Gradient */}
              <div className={`relative h-48 bg-gradient-to-br ${caseStudy.gradient} flex items-center justify-center`}>
                <div className="text-white text-center">
                  <h3 className="text-xl font-bold mb-2">{caseStudy.title}</h3>
                  <p className="text-sm opacity-90">{caseStudy.subtitle}</p>
                </div>
                <div className="absolute top-4 right-4 bg-green-500 text-white px-3 py-1 rounded-full text-xs font-bold">
                  {caseStudy.status}
                </div>
              </div>

              {/* Content */}
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-sm text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
                    {caseStudy.duration}
                  </span>
                  <span className="text-sm font-semibold text-green-600">
                    {caseStudy.margin}
                  </span>
                </div>

                <p className="text-gray-600 mb-6 leading-relaxed">
                  {caseStudy.description}
                </p>

                {/* Vote Share Progress */}
                <div className="mb-6">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm text-gray-600">Vote Share Progress</span>
                    <span className="text-sm font-semibold text-gray-900">
                      {caseStudy.previousVote}% → {caseStudy.achievedVote}%
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-3">
                    <div
                      className="bg-gradient-to-r from-blue-500 to-green-500 h-3 rounded-full transition-all duration-400"
                      style={{ width: `${caseStudy.achievedVote}%` }}
                    ></div>
                  </div>
                </div>

                {/* Call to Action */}
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500">Click to view detailed case study</span>
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center group-hover:bg-blue-200 transition-colors">
                    <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Modal for Case Study Details */}
        {selectedCase && (
          <div
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={closeModal}
          >
            <div
              className="bg-white rounded-3xl max-w-4xl w-full max-h-[90vh] overflow-y-auto p-8"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900">{selectedCase.title} - Detailed Case Study</h2>
                <button
                  onClick={closeModal}
                  className="text-gray-500 hover:text-gray-700 text-2xl"
                >
                  ×
                </button>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-xl font-semibold mb-4">Campaign Overview</h3>
                  <p className="text-gray-600 mb-6">{selectedCase.details.overview}</p>

                  <h4 className="font-semibold mb-3">Key Strategies Implemented:</h4>
                  <ul className="space-y-2 text-gray-600 mb-6">
                    {selectedCase.details.strategies.map((strategy, index) => (
                      <li key={index}>• {strategy}</li>
                    ))}
                  </ul>
                </div>

                <div>
                  <h3 className="text-xl font-semibold mb-4">Results Achieved</h3>
                  <ul className="space-y-2 text-gray-600 mb-6">
                    {selectedCase.details.results.map((result, index) => (
                      <li key={index} className="flex items-start">
                        <span className="text-green-500 mr-2">✓</span>
                        {result}
                      </li>
                    ))}
                  </ul>

                  <h4 className="font-semibold mb-3">Client Testimonial:</h4>
                  <blockquote className="bg-gray-50 p-4 rounded-lg italic text-gray-700">
                    "{selectedCase.details.testimonial.text}"
                    <footer className="text-sm text-gray-500 mt-2">- {selectedCase.details.testimonial.author}</footer>
                  </blockquote>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default SuccessfulCampaignCaseStudies;
